import React, { useEffect, useRef } from 'react';
import apiService from '../services/ApiService';
import { getViewSessionToken } from '../utils/viewSession';

interface BuildViewTrackerProps {
  vehicleId: number;
  /** e.g. "qr" from ?ref=qr */
  source?: string | null;
  /** Element to observe; defaults to document body via a sentinel rooted at this component. */
  rootMargin?: string;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Counts a public build view once per page instance when ≥50% visible for ≥1s.
 * Posts to vehicle_view.php (server handles daily dedupe).
 */
export const BuildViewTracker: React.FC<BuildViewTrackerProps> = ({
  vehicleId,
  source,
  rootMargin = '0px',
  children,
  className = '',
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const recordedRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    recordedRef.current = false;
    const el = rootRef.current;
    if (!el || !vehicleId) return;

    const clearTimer = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const record = () => {
      if (recordedRef.current) return;
      recordedRef.current = true;
      clearTimer();
      void apiService.recordVehicleView({
        vehicle_id: vehicleId,
        session_token: getViewSessionToken(),
        source: source || null,
      });
    };

    if (typeof IntersectionObserver === 'undefined') {
      // Fallback: record after 1s on mount when IO unavailable
      timerRef.current = window.setTimeout(record, 1000);
      return () => clearTimer();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (timerRef.current == null && !recordedRef.current) {
            timerRef.current = window.setTimeout(record, 1000);
          }
        } else {
          clearTimer();
        }
      },
      { threshold: [0, 0.5, 1], rootMargin }
    );

    observer.observe(el);
    return () => {
      clearTimer();
      observer.disconnect();
    };
  }, [vehicleId, source, rootMargin]);

  return (
    <div ref={rootRef} className={className || undefined} data-build-view-tracker={vehicleId}>
      {children}
    </div>
  );
};

export default BuildViewTracker;
